import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { useFirePostOfficeTrigger } from '@atlassian/post-office-context/post-office-trigger';

import {
	getContentKeywords,
	getTitleKeywords,
} from '@confluence/experiment-meeting-signal-core/entry-points/meetingSignalKeywords.ts';
import { matchMeetingSignal } from '@confluence/experiment-meeting-signal-core/entry-points/matchMeetingSignal.ts';
import { matchesMeetingTemplate } from '@confluence/experiment-meeting-signal-core/entry-points/matchesMeetingTemplate.ts';
import type {
	MatchedMeetingSignal,
	MeetingSignalInput,
	MeetingSignalSource,
	MeetingSignalTrigger,
} from '@confluence/experiment-meeting-signal-core/entry-points/meetingSignalTypes.ts';
import { expVal } from '@confluence/feature-experiments';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import { useIsHighTouchTwcSignup } from './useIsHighTouchTwcSignup.ts';
import { useMeetingRecorderEligibility } from './useMeetingRecorderEligibility.ts';

type MeetingSignalRenderState =
	| { matched: false; matchedSignal: null; trigger: null; source: null }
	| {
			matched: true;
			matchedSignal: MatchedMeetingSignal;
			trigger: MeetingSignalTrigger;
			source: MeetingSignalSource;
	  };

const INITIAL_STATE: MeetingSignalRenderState = {
	matched: false,
	matchedSignal: null,
	trigger: null,
	source: null,
};

const getMatchedSignal = (
	input: MeetingSignalInput,
	titleKeywords: readonly string[],
	contentKeywords: readonly string[],
): MatchedMeetingSignal | null => {
	if (input.titleText && matchMeetingSignal(input.titleText, titleKeywords)) {
		return 'title';
	}
	if (input.contentText && matchMeetingSignal(input.contentText, contentKeywords)) {
		return 'content';
	}
	if (matchesMeetingTemplate(input.template)) {
		return 'template';
	}
	return null;
};

export type UseMeetingSignalExperimentResult = MeetingSignalRenderState & {
	evaluate: (input: MeetingSignalInput) => void;
};

/**
 * Checks whether a meeting signal can show a nudge.
 *
 * The hook reads the cohort gate and preloaded Meeting Recorder eligibility. It then checks the
 * meeting signal and exposes the HT or LT experiment. The hook keeps the first treatment match for
 * the current page load.
 *
 * A title-edit match shows the title nudge. All other matches trigger the side-nav nudge.
 */
export function useMeetingSignalExperiment(): UseMeetingSignalExperimentResult {
	const intl = useIntl();
	const titleKeywords = useMemo(() => getTitleKeywords(intl), [intl]);
	const contentKeywords = useMemo(() => getContentKeywords(intl), [intl]);
	const { isHighTouch } = useIsHighTouchTwcSignup();
	const renderServerPlaceholder = useRenderServerPlaceholder();

	// Recorder eligibility owns the cohort-specific rollout gate check before reading preload data.
	const { isAvailable } = useMeetingRecorderEligibility({
		isHighTouch: renderServerPlaceholder ? null : isHighTouch,
	});
	const { fireTrigger } = useFirePostOfficeTrigger({
		triggerId: 'twc-loom-meeting-nudge-sidenav-trigger',
	});

	// Attach points can report a signal before cohort and recorder eligibility have resolved.
	// Retain the latest candidate so the effect can evaluate it once those prerequisites are ready.
	const [signalInputToEvaluate, setSignalInputToEvaluate] = useState<MeetingSignalInput | null>(
		null,
	);
	const [state, setState] = useState<MeetingSignalRenderState>(INITIAL_STATE);
	const hasMatchedAlreadyRef = useRef(false);

	const evaluate = useCallback((input: MeetingSignalInput) => {
		if (!hasMatchedAlreadyRef.current) {
			setSignalInputToEvaluate(input);
		}
	}, []);

	useEffect(() => {
		if (!isAvailable || !signalInputToEvaluate || hasMatchedAlreadyRef.current) {
			return;
		}

		const matchedSignal = getMatchedSignal(signalInputToEvaluate, titleKeywords, contentKeywords);
		if (!matchedSignal) {
			return;
		}

		if (
			isHighTouch === true
				? expVal('twcg-2202-loom-meeting-nudge-ht', 'isEnabled', false) !== true
				: expVal('twcg-2202-loom-meeting-nudge-lt', 'isEnabled', false) !== true
		) {
			return;
		}

		hasMatchedAlreadyRef.current = true;
		setState({
			matched: true,
			matchedSignal,
			trigger: signalInputToEvaluate.trigger,
			source: signalInputToEvaluate.source,
		});

		if (signalInputToEvaluate.trigger !== 'titleEdit') {
			fireTrigger({ isActive: true });
		}
	}, [
		contentKeywords,
		fireTrigger,
		isAvailable,
		isHighTouch,
		signalInputToEvaluate,
		titleKeywords,
	]);

	return { ...state, evaluate };
}
